// Which values a column may hold alongside the ones it already holds.
//
// Ported from `actualsPlusBudgetAllowedOptions` and `ytdOnlyOptions` in
// app/assets/javascripts_deprecated/report_editor/report_column_list.js.coffee —
// the Report Settings column editor. Both editors write the same columns, so
// both have to apply the same rules. Only the actuals-plus-budget half is
// enforced server-side (Report::PeriodLogic#enforce_actuals_plus_budget_options
// rewrites those fields on save); the YTD-only half lives in the form alone.

import type { ColumnValues, EditorColumn, TypeSchema } from './types'

// A value type of "actuals plus budget" can carry a budget set id
// ("actuals_plus_budget-42"), so it is matched by prefix.
const ACTUALS_PLUS_BUDGET = 'actuals_plus_budget'

// Actuals plus budget is a full-book-year figure, so choosing it settles the
// fields that would otherwise say which part of which year to show. Keyed by the
// field that carries the choice: `show` on a period column, and one per operand
// on a difference column.
const ACTUALS_PLUS_BUDGET_ALLOWED: Record<string, Record<string, string[]>> = {
  show: {
    relative: ['YTD', 'RYTD'],
    delta: ['0'],
    periods: ['last_period_of_current_year'],
  },
  periods_value_type: {
    delta: ['0'],
    periods: ['last_period_of_year'],
  },
  periods_2_value_type: {
    delta_2: ['0'],
    periods_2: ['last_period_of_year'],
  },
}

// A difference column has one basis for both operands, so either of them being
// actuals plus budget settles it.
const DIFFERENCE_OPERAND_FIELDS = ['periods_value_type', 'periods_2_value_type']
const ACTUALS_PLUS_BUDGET_ALLOWED_BASIS = { relative: ['YTD', 'RYTD'] }

// Adjustments — and actuals read without them — only mean anything cumulatively,
// so they are off the table unless the column is year-to-date.
const YTD_ONLY_SHOW_VALUES = [
  'adjustments_internal',
  'adjustments_external',
  'adjustments_combined',
  'actuals_without_adjustments',
]

// Values a constrained field may currently take, keyed by field. A field the
// column's other values say nothing about is absent rather than listed in full:
// absent means "every option, and the control stays open", which is what the
// drawer needs to tell apart from "one option left, so there is nothing to pick".
export type FieldAvailability = Record<string, string[]>

export function fieldAvailability(
  column: EditorColumn,
  schema?: TypeSchema
): FieldAvailability {
  if (!schema) return {}

  const allowed = allowedValues(column.values)
  const availability: FieldAvailability = {}

  schema.fields.forEach((field) => {
    const enabled = field.options
      .map((option) => option.value)
      .filter((value) => isAvailable(field.key, value, column.values, allowed))

    // Only fields the rules actually narrow: comparing against the full option
    // list keeps a field that happens to offer one option from reading as one
    // the rules closed down.
    if (enabled.length !== field.options.length)
      availability[field.key] = enabled
  })

  return availability
}

// The values to write back so the column stops contradicting itself: a field
// holding something its own values have just ruled out falls back to the first
// value still open to it. Mirrors the coffee editor, which rewrites the select
// and fires a change rather than leaving an impossible column on screen.
export function constrainedValues(
  column: EditorColumn,
  schema?: TypeSchema
): ColumnValues {
  const corrections: ColumnValues = {}
  const values = { ...column.values }

  // A correction can open or close another field — landing on RYTD rules out the
  // adjustment `show` values, say — so this settles rather than passes once. The
  // field count bounds it: every pass fixes at least one field for good.
  const passes = schema?.fields.length ?? 0

  for (let pass = 0; pass < passes; pass += 1) {
    const availability = fieldAvailability({ ...column, values }, schema)

    const offending = Object.entries(availability).find(
      ([key, enabled]) =>
        values[key] !== undefined &&
        enabled.length > 0 &&
        !enabled.includes(values[key])
    )
    if (!offending) break

    const [key, enabled] = offending
    corrections[key] = enabled[0]
    values[key] = enabled[0]
  }

  return corrections
}

// Where a column lands once the rules have had their say, and which of its values
// they displaced getting there.
//
// A rule that overwrites a field has to be undoable: picking actuals plus budget
// pins the period to the whole book year, and picking something else again has to
// give the user back the period they had chosen rather than leave them on a
// config they never asked for — which previews as a column with no data, so it
// reads as the editor having broken.
export function settleColumn(
  column: EditorColumn,
  schema?: TypeSchema,
  editedKey?: string
): { values: ColumnValues; displaced: ColumnValues } {
  const values = { ...column.values }
  const displaced = { ...column.displaced }

  Object.entries(constrainedValues({ ...column, values }, schema)).forEach(
    ([key, corrected]) => {
      // Only the value the user last chose for the field is worth keeping: a
      // second pin over an already-displaced field must not bury it.
      if (!(key in displaced)) displaced[key] = values[key]
      values[key] = corrected
    }
  )

  const availability = fieldAvailability({ ...column, values }, schema)
  const restored = Object.keys(displaced)
    .filter((key) => !availability[key])
    .filter(
      (key) =>
        !forcesCorrection(column, values, displaced, key, schema, editedKey)
    )

  restored.forEach((key) => {
    values[key] = displaced[key]
    delete displaced[key]
  })

  // A restored value can be one that something else rules out — a PTD basis with
  // an adjustments `show` — so let the rules settle over it as well.
  if (restored.length > 0) {
    Object.assign(values, constrainedValues({ ...column, values }, schema))
  }

  return { values, displaced }
}

// Whether giving a field its displaced value back would cost the user a value
// they chose. A restore the column can take as it stands is free; one that leaves
// the rules with another field to rewrite is not, because that rewrite lands in
// the settle above, which records nothing — so whatever it overwrites is gone,
// whether it is the field just edited or one the user set several clicks ago.
// Restoring a PTD basis under a freshly picked adjustments `show` is the plain
// case: it rules that `show` straight back out. The values on screen win; the
// older one stays displaced, and comes back the moment the field it contradicts
// is picked again.
function forcesCorrection(
  column: EditorColumn,
  values: ColumnValues,
  displaced: ColumnValues,
  key: string,
  schema?: TypeSchema,
  editedKey?: string
): boolean {
  if (!editedKey) return false

  const candidate = { ...values, [key]: displaced[key] }

  return (
    Object.keys(constrainedValues({ ...column, values: candidate }, schema))
      .length > 0
  )
}

// The narrowings the column's current values impose, merged. Two of the
// actuals-plus-budget entries do narrow the same fields — `show` and
// `periods_value_type` both name `delta` and `periods`, and disagree over which
// period — but no column type carries both keys: `show` belongs to period and
// analytical columns, the operand value types to difference ones (see
// ColumnsDrawerPresenter). So a column can only ever trip one of them, and the
// merge below never has to choose. Giving a type both would make it
// last-writer-wins.
function allowedValues(values: ColumnValues): Record<string, string[]> {
  const allowed: Record<string, string[]> = {}

  Object.entries(ACTUALS_PLUS_BUDGET_ALLOWED).forEach(([key, narrowings]) => {
    if (isActualsPlusBudget(values[key])) Object.assign(allowed, narrowings)
  })

  if (
    DIFFERENCE_OPERAND_FIELDS.some((key) => isActualsPlusBudget(values[key]))
  ) {
    Object.assign(allowed, ACTUALS_PLUS_BUDGET_ALLOWED_BASIS)
  }

  return allowed
}

function isAvailable(
  key: string,
  value: string,
  values: ColumnValues,
  allowed: Record<string, string[]>
): boolean {
  if (allowed[key] && !allowed[key].includes(value)) return false
  if (key === 'show' && values.relative !== 'YTD' && isYtdOnly(value)) {
    return false
  }

  return true
}

const isActualsPlusBudget = (value?: string): boolean =>
  !!value && value.startsWith(ACTUALS_PLUS_BUDGET)

// Matched on the base value: these carry no budget set, but a `show` that does
// would arrive with the id appended.
const isYtdOnly = (value: string): boolean =>
  YTD_ONLY_SHOW_VALUES.includes(value.replace(/-\d+$/, ''))
